/**
 * Slash-command input parser
 *
 * Takes the raw palette input string and returns a structured ParsedSlash
 * describing which phase the user is in and what they've typed so far.
 */

export type SlashPhase =
  /** user typed "/" or partial verb like "/ai" (no space yet) */
  | 'verb'
  /** user typed verb + space, partial or empty sub like "/ai " or "/ai cr" */
  | 'sub'
  /** user typed verb + sub + space, optional free text like "/ai create " */
  | 'arg'
  /** fully-formed and ready to execute (sub is known + arg if required) */
  | 'complete';

export interface ParsedSlash {
  /** The full raw input string */
  raw: string;
  /** The verb portion, lowercased, e.g. "ai" */
  verb: string | null;
  /** The sub-command portion, lowercased, e.g. "create" */
  sub: string | null;
  /** The free-text argument portion (everything after sub + space) */
  arg: string | null;
  /** Current parsing phase */
  phase: SlashPhase;
}

/**
 * Parse a raw input string into a ParsedSlash.
 *
 * Rules:
 *  "/ai"        → verb="ai"   sub=null    arg=null   phase='verb'
 *  "/ai "       → verb="ai"   sub=""      arg=null   phase='sub'
 *  "/ai cr"     → verb="ai"   sub="cr"    arg=null   phase='sub'
 *  "/ai create" → verb="ai"   sub="create" arg=null  phase='sub'  (no trailing space)
 *  "/ai create " → verb="ai"  sub="create" arg=""    phase='arg'
 *  "/ai create foo" → verb="ai" sub="create" arg="foo" phase='arg'
 */
export function parseSlash(input: string): ParsedSlash {
  if (!input.startsWith('/')) {
    return { raw: input, verb: null, sub: null, arg: null, phase: 'verb' };
  }

  const withoutSlash = input.slice(1);

  // Find the first space — separates verb from the rest
  const firstSpace = withoutSlash.indexOf(' ');
  if (firstSpace === -1) {
    // Only verb typed, no space
    return {
      raw: input,
      verb: withoutSlash.toLowerCase() || null,
      sub: null,
      arg: null,
      phase: 'verb',
    };
  }

  const verb = withoutSlash.slice(0, firstSpace).toLowerCase();
  const afterVerb = withoutSlash.slice(firstSpace + 1); // everything after "verb "

  // Find the second space — separates sub from arg
  const secondSpace = afterVerb.indexOf(' ');
  if (secondSpace === -1) {
    // sub may be partial or complete, but no arg yet
    return {
      raw: input,
      verb,
      sub: afterVerb.toLowerCase(),
      arg: null,
      phase: 'sub',
    };
  }

  const sub = afterVerb.slice(0, secondSpace).toLowerCase();
  const arg = afterVerb.slice(secondSpace + 1); // everything after "sub "

  return {
    raw: input,
    verb,
    sub,
    arg,
    phase: 'arg',
  };
}

/**
 * Returns the input string with ghost text accepted up to the next segment.
 *
 * Tab behaviour:
 * - Phase 'verb': complete the verb + add a space
 * - Phase 'sub' (partial): complete the sub + add a space
 * - Phase 'sub' (exact match): just add a space (to advance to arg phase)
 * - Phase 'arg': no-op (free text, nothing to complete)
 */
export function acceptGhostCompletion(
  input: string,
  bestVerbMatch: string | null,
  bestSubMatch: string | null,
): string {
  const parsed = parseSlash(input);
  if (!parsed.verb && !input.startsWith('/')) return input;

  if (parsed.phase === 'verb') {
    if (!bestVerbMatch) return input;
    return `/${bestVerbMatch} `;
  }

  if (parsed.phase === 'sub') {
    if (!bestSubMatch) return input;
    const verbPart = `/${parsed.verb} `;
    // If sub is already an exact match, just append a space
    if (parsed.sub === bestSubMatch) {
      return `${verbPart}${bestSubMatch} `;
    }
    return `${verbPart}${bestSubMatch} `;
  }

  return input;
}
